import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  Typography,
  useTheme,
} from "@mui/material";
import { NavbarIcon } from "../../../atoms/navbar/Icon";
import { SmallModal } from "../../../components/SmallModal";
import { useMetricResults } from "../../../hooks/useMetricResults";
import { useMetric } from "../../../hooks/useMetrics";
import { defaultDateDisplay, isJSON } from "../../../utilities/UIHelper";
import { IColumnVisibility } from "../../../molecules/ColumnVisibility";
import { MetricResult } from "../../../models/types";
import { JSONView } from "../../../molecules/JSONView";
import { TableTextWithEllipsis } from "../../../molecules/TableTextWithEllipsis";
import { APITable } from "../../APITable";
import { EmptyTable } from "../../../molecules/EmptyTable";
import SecondaryBox from "../../../atoms/box/SecondaryBox";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import CustomCalendar from "../../../molecules/CustomCalendar";
import { Chart } from "../../chart/chart";
import { GraphLoader } from "../../skeleton-loaders/GraphLoader";
import { useEffect, useRef, useState } from "react";
import { transformMetricResultsToChartData } from "../../../utilities/GraphDataHelper";
import { ShowMore } from "../../../molecules/ShowMore";
import { DefaultBadge } from "../../../atoms/StatusBadge";
import { navigateToMetricInNewTab } from "../../../components/TestNavigator";

type KPIDetailsModalProps = {
  open: boolean;
  onClose: () => void;
  metricId: string;
  parentObjectId: string;
};

export const KPIDetailsModal = (props: KPIDetailsModalProps) => {
  const { open, onClose, metricId, parentObjectId } = props;
  const parentRef = useRef(null);
  const theme = useTheme();
  const { data: metric, isLoading: isLoadingMetric } = useMetric(metricId, { fairo_data: true });
  const [timeFilter, setTimeFilter] = useState<string>("Days");
  const [selectedStartDate, setSelectedStartDate] = useState<string>(
    moment().subtract(7, "days").format("YYYY-MM-DD")
  );
  const [selectedEndDate, setSelectedEndDate] = useState<string>(moment().format("YYYY-MM-DD"));
  const { data: allResults, isLoading: isLoadingAllResults } = useMetricResults({
    "metric[]": [metricId],
    related_use_case: parentObjectId,
    page_size: 1,
  });
  const { data: metricResults, isLoading: isLoadingMetricResults } = useMetricResults({
    metric: [metricId],
    related_use_case: parentObjectId,
    "created_on_from[]": moment(selectedStartDate).format("YYYY-MM-DD"),
    "created_on_to[]": moment(selectedEndDate).format("YYYY-MM-DD"),
  });
  const reducers = ["Count", "Average", "Sum"];
  const [reducerFilter, setReducerFilter] = useState<string[]>(["Count"]);
  const graphId = "kpi-chart";
  const chart = ApexCharts.getChartByID(graphId);

  const { lineChartData } = transformMetricResultsToChartData(
    metricResults?.results,
    timeFilter,
    reducers
  );

  const columnsVisibility: IColumnVisibility<MetricResult>[] = [
    {
      field: "value",
      headerName: "Result Value",
      visible: true,
      columnMinWidth: 200,
      columnMaxWidth: 200,
      renderer: (result) => {
        const isNumber = !isNaN(Number(result.value));
        return isJSON(result.value) && !isNumber ? (
          <Box onClick={(e) => e.stopPropagation()}>
            <JSONView
              value={typeof result.value === "string" ? result.value : JSON.stringify(result.value)}
            />
          </Box>
        ) : (
          <TableTextWithEllipsis value={result?.value} />
        );
      },
    },
    {
      field: "updated_on",
      headerName: "Updated On",
      visible: true,
      columnMinWidth: 110,
      columnMaxWidth: 110,
      renderer: (result) => defaultDateDisplay(result.updated_on),
    },
  ];

  const handleReducerFilterChange = (unselectedSeries: number[]) => {
    const newReducerFilter = reducers.filter((_, index) => !unselectedSeries.includes(index));
    setReducerFilter(newReducerFilter);
  };

  useEffect(() => {
    if (allResults?.count && metricResults?.count === 0) {
      const lastResult = allResults.results[0];
      setSelectedEndDate(moment(lastResult.updated_on).format("YYYY-MM-DD"));
      setSelectedStartDate(moment(lastResult.updated_on).subtract(7, "days").format("YYYY-MM-DD"));
    }
  }, [allResults]);

  useEffect(() => {
    try {
      if (chart) {
        reducers.forEach((reducer, index) => {
          if (!reducerFilter.includes(reducer)) {
            chart.hideSeries(reducer);
          }
        });
      }
    } catch {}
  }, [chart, lineChartData, open]);

  return (
    <SmallModal
      open={open}
      onClose={onClose}
      title=""
      showCloseButton={false}
      size="medium"
      position="right"
      hideContentPadding
      showTitleContentDivider
      customTitle={
        <Box display="flex" flexDirection="row" gap="10px" alignItems="center">
          <Box
            onClick={() => onClose()}
            sx={{ cursor: "pointer" }}
            display="flex"
            alignItems="center"
          >
            <NavbarIcon
              variant="left-arrow"
              sx={{ width: "22px", minWidth: "22px", height: "22px", minHeight: "22px" }}
            />
          </Box>
          <Typography variant="h2">{metric?.name}</Typography>
        </Box>
      }
      action={
        <Box padding="6px 16px 16px 16px" width="100%">
          <Button fullWidth={true} onClick={() => navigateToMetricInNewTab(metric)}>
            More Details
          </Button>
        </Box>
      }
    >
      <Box padding="16px" display="flex" flexDirection="column" gap="16px">
        <Box display="flex" gap="10px" justifyContent="space-between">
          <Typography variant="h3">Category</Typography>
          <DefaultBadge value={metric?.category_name?.name ?? "-"} />
        </Box>
        <Typography variant="h3">Description</Typography>
        <ShowMore description={metric?.description ?? ""} maxChar={250} richText={true} />
        <SecondaryBox padding="0px">
          <Box
            display="flex"
            flexDirection="column"
            bgcolor={theme.palette.custom.primaryBackground}
            width="100%"
            height="355px"
            padding="0px"
            sx={{ borderRadius: "6px" }}
          >
            {isLoadingAllResults || isLoadingMetricResults ? (
              <GraphLoader
                reducerFilter={true}
                timeFilter={true}
                dateFilter={true}
                variant="rectangular"
                width="100%"
              />
            ) : (
              <>
                <Box
                  display="flex"
                  flexDirection={{ xs: "column", sm: "row" }} // Use column layout on xs (mobile) screens, and row layout on sm (small) screens and above
                  padding="10px"
                  justifyContent="start"
                  alignItems="start"
                  gap="10px"
                  sx={{
                    borderRadius: 0,
                    borderBottom: "1px solid",
                    borderBottomColor: theme.palette.custom.secondaryBorder,
                  }}
                >
                  <FormControl style={{ width: "33%", maxWidth: "170px" }}>
                    <Select
                      labelId="days-label"
                      id="days-select"
                      value={timeFilter}
                      onChange={(event) => setTimeFilter(event.target.value)}
                    >
                      <MenuItem value={"Days"}>Days</MenuItem>
                      <MenuItem value={"Week"}>Week</MenuItem>
                      <MenuItem value={"Month"}>Month</MenuItem>
                    </Select>
                  </FormControl>
                  <Box margin={{ xs: "10px 0", sm: "0 10px" }}>
                    <LocalizationProvider dateAdapter={AdapterMoment} style={{ width: "33%" }}>
                      <CustomCalendar value={selectedStartDate} onChange={setSelectedStartDate} />
                    </LocalizationProvider>
                  </Box>
                  <Box alignSelf="center">To</Box>
                  <Box margin={{ xs: "10px 0", sm: "0 10px" }}>
                    <LocalizationProvider dateAdapter={AdapterMoment} style={{ width: "33%" }}>
                      <CustomCalendar value={selectedEndDate} onChange={setSelectedEndDate} />
                    </LocalizationProvider>
                  </Box>
                </Box>
                <Box
                  overflow="hidden"
                  display="flex"
                  flexGrow="1"
                  padding="10px"
                  alignItems="center"
                  justifyContent="center"
                  ref={parentRef}
                >
                  {isLoadingAllResults || isLoadingMetricResults ? (
                    <CircularProgress />
                  ) : (
                    <Chart
                      data={lineChartData[0].values}
                      labels={lineChartData[0].labels}
                      type="line"
                      tooltip=""
                      width="100%"
                      onClick={() => {}}
                      borderBottom="0px"
                      borderLeft="0px"
                      borderRight="0px"
                      borderTop="0px"
                      colors={[
                        theme.palette.custom.greenTypography,
                        theme.palette.custom.redTypography,
                        theme.palette.custom.blueTypography,
                      ]}
                      xAxisTitle={timeFilter}
                      min={(min) => min - 1}
                      max={(max) => max + 1}
                      onLegendClick={(series: number[]) => {
                        handleReducerFilterChange(series);
                      }}
                      id={graphId}
                    />
                  )}
                </Box>
              </>
            )}
          </Box>
        </SecondaryBox>
        <APITable
          collapsable
          title="Metric Results"
          useGetData={useMetricResults}
          queryParams={{ related_use_case: parentObjectId, metric: [metricId] }}
          columnsVisibility={columnsVisibility}
          smallEmptyHeight={true}
          emptyTableComponent={
            <EmptyTable
              variant="metrics-empty"
              label="Metric Results"
              description={`Add a result to see the data here.`}
            />
          }
          tableSize="small"
          hideColumnVisibility
        />
      </Box>
    </SmallModal>
  );
};
